import { BACKEND_URL } from '@/config/api';
// src/components/Creator/AuthorProfileCard.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Globe, Eye, EyeOff, Edit, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AuthorProfileForm from './AuthorProfileForm';

const AuthorProfileCard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${BACKEND_URL}/api/creator/author-profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.exists === false) {
        setProfile(null);
      } else {
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Error fetching author profile:', error);
      toast.error('Failed to load author profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleFormClose = () => {
    setShowForm(false);
    fetchProfile(); // Refresh profile data
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${BACKEND_URL}/api/creator/author-profile/generate-ai`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfile(response.data);
      toast.success('AI-Enhanced Profile Generated!');
    } catch (error) {
      console.error('Error generating AI profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI profile');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-6">
          <div className="text-center text-slate-400">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  // Empty state - no profile yet
  if (!profile) {
    return (
      <>
        <Card className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <User className="h-5 w-5" />
              Author Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="mb-4">
                <User className="h-16 w-16 mx-auto text-slate-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Introduce Yourself to Your Readers
              </h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Create your Author Profile to help readers connect with you and discover your work.
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Create Author Profile
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {showForm && (
          <AuthorProfileForm
            isOpen={showForm}
            onClose={handleFormClose}
            profile={null}
          />
        )}
      </>
    );
  }

  // Profile exists - show summary
  const displayName = profile.pen_name || `${profile.first_name} ${profile.last_name}`;
  const bioPreview = profile.bio_raw_source?.text?.substring(0, 200) || 'No bio yet';

  return (
    <>
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <User className="h-5 w-5" />
              Author Profile
            </CardTitle>
            <div className="flex items-center gap-2">
              {profile.is_public ? (
                <span className="flex items-center gap-1 text-sm text-green-400">
                  <Eye className="h-4 w-4" />
                  Public
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-slate-400">
                  <EyeOff className="h-4 w-4" />
                  Private
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Author info */}
            <div className="flex items-start gap-4">
              {/* Author Headshot */}
              <div className="relative shrink-0">
                {profile.headshot_image_url ? (
                  <img
                    src={`${BACKEND_URL}${profile.headshot_image_url}?t=${Date.now()}`}
                    alt={displayName}
                    className="h-16 w-16 rounded-full object-cover border-2 border-purple-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`h-16 w-16 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center border-2 border-purple-500 ${profile.headshot_image_url ? 'hidden' : ''}`}
                >
                  <span className="text-xl font-bold text-white">
                    {(profile.first_name?.[0] || 'A').toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">{displayName}</h3>
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <Globe className="h-3 w-3" />
                    {profile.website_url}
                  </a>
                )}
              </div>
            </div>

            {/* Bio preview */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <p className="text-sm text-slate-300">
                {bioPreview}{bioPreview.length >= 200 && '...'}
              </p>
            </div>

            {/* Status indicators */}
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span className="px-2 py-1 bg-slate-700 rounded">
                {profile.status === 'published' ? 'Published' : 'Draft'}
              </span>
              {profile.bio_raw_source?.type === 'uploaded' && (
                <span className="px-2 py-1 bg-slate-700 rounded">
                  Bio: Uploaded
                </span>
              )}
            </div>

            {/* AI Enhancement Section */}
            {profile.bio_ai_enhanced?.short_bio ? (
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-purple-400" />
                  <span className="text-sm font-semibold text-purple-300">
                    AI-Enhanced Profile Available
                  </span>
                </div>
                <p className="text-xs text-slate-400 mb-2">
                  Last generated: {new Date(profile.bio_ai_enhanced.last_generated_at).toLocaleDateString()}
                </p>
                {profile.bio_ai_enhanced.tagline && (
                  <p className="text-sm text-slate-300 italic">
                    "{profile.bio_ai_enhanced.tagline}"
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-3">
                <p className="text-sm text-slate-300 mb-2">
                  Generate a polished, professional author profile using AI
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setShowForm(true)}
                size="sm"
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                <Edit className="h-4 w-4" />
                Edit Profile
              </Button>
              
              {profile.bio_ai_enhanced?.short_bio && (
                <Button
                  onClick={() => navigate('/creator/author-profile/view')}
                  size="sm"
                  className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  <Eye className="h-4 w-4" />
                  View Author Profile
                </Button>
              )}
              
              <Button
                onClick={handleGenerateAI}
                disabled={generating}
                size="sm"
                className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:opacity-50 text-white font-semibold"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {profile.bio_ai_enhanced?.short_bio ? 'Regenerate' : 'Generate'} AI Profile
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <AuthorProfileForm
          isOpen={showForm}
          onClose={handleFormClose}
          profile={profile}
        />
      )}
    </>
  );
};

export default AuthorProfileCard;
